import { env } from "@/config/env";
import { fidexaHubNotificationService } from "./fidexaHubNotificationService";
import type {
  FidexaHubPushPlatform,
  FidexaHubPushSubscriptionPayload,
} from "./fidexaHubNotificationType";

// Push do navegador (Web Push) do FidexaHub. O service worker é o mesmo
// public/sw.js do app (sem cache nenhum, ver o comentário dele).

// Lida pelo script inline do index.html: no desktop ele remove todo service
// worker a cada carregamento (bug antigo da "tela piscando"). Com o push
// ligado neste navegador, o SW precisa ficar, senão a inscrição morre junto.
export const PUSH_ENABLED_STORAGE_KEY = "fidexa_hub_push_enabled";

export type PushSupport =
  // Navegador sem Service Worker/Push API.
  | "unsupported"
  // iPhone/iPad no Safari (fora da Tela de Início): push só existe no app
  // adicionado à Tela de Início (iOS 16.4+).
  | "ios-needs-install"
  // Bloqueado nas configurações do navegador: só o cliente destrava.
  | "denied"
  // Pode pedir (nunca perguntado) ou já concedido.
  | "available";

const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (/macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export const getPushSupport = (): PushSupport => {
  const hasApis =
    "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

  if (isIOS() && !isStandalone()) return "ios-needs-install";
  if (!hasApis) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  return "available";
};

export const getPushPlatform = (): FidexaHubPushPlatform => {
  if (isIOS()) return "web_ios";
  if (/android/i.test(navigator.userAgent)) return "web_android";
  return "web_desktop";
};

const setPushEnabledFlag = (enabled: boolean) => {
  try {
    if (enabled) localStorage.setItem(PUSH_ENABLED_STORAGE_KEY, "1");
    else localStorage.removeItem(PUSH_ENABLED_STORAGE_KEY);
  } catch {
    // Storage bloqueado: no pior caso o desktop perde o SW no próximo load.
  }
};

// O SW não tem acesso ao env do Vite; recebe a URL da API por mensagem pra
// avisar o backend quando a notificação chega e quando é clicada.
const sendConfigToServiceWorker = (registration: ServiceWorkerRegistration) => {
  const worker = registration.active ?? registration.waiting ?? registration.installing;
  worker?.postMessage({ type: "fidexa-hub-config", apiUrl: env.API_URL });
};

const getRegistration = async (): Promise<ServiceWorkerRegistration> => {
  const existing = await navigator.serviceWorker.getRegistration("/");
  if (!existing) {
    await navigator.serviceWorker.register("/sw.js");
  }
  return navigator.serviceWorker.ready;
};

const base64UrlToUint8Array = (value: string): Uint8Array<ArrayBuffer> => {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
};

const arrayBufferToBase64Url = (buffer: ArrayBuffer | null): string => {
  if (!buffer) return "";
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach(byte => (binary += String.fromCharCode(byte)));
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const toPayload = (subscription: PushSubscription): FidexaHubPushSubscriptionPayload => ({
  endpoint: subscription.endpoint,
  p256dh: arrayBufferToBase64Url(subscription.getKey("p256dh")),
  auth: arrayBufferToBase64Url(subscription.getKey("auth")),
  platform: getPushPlatform(),
  userAgent: navigator.userAgent,
});

/** Inscrição deste navegador, se houver (sem pedir nada ao cliente). */
export const getCurrentSubscription = async (): Promise<PushSubscription | null> => {
  if (getPushSupport() === "unsupported" || !("serviceWorker" in navigator)) return null;
  const registration = await navigator.serviceWorker.getRegistration("/");
  if (!registration) return null;
  sendConfigToServiceWorker(registration);
  return registration.pushManager.getSubscription();
};

/**
 * Pede a permissão do navegador (TEM que ser chamado a partir de um clique:
 * o Safari recusa fora de gesto do usuário) e cria a inscrição.
 * Devolve null quando o cliente não concede.
 */
export const subscribeToPush = async (
  publicKey: string
): Promise<FidexaHubPushSubscriptionPayload | null> => {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return null;

  const registration = await getRegistration();
  sendConfigToServiceWorker(registration);

  const applicationServerKey = base64UrlToUint8Array(publicKey);
  let subscription = await registration.pushManager.getSubscription();

  // Inscrição feita com outra chave VAPID (troca de chave no servidor) não
  // serve mais: recria.
  const currentKey = subscription?.options.applicationServerKey;
  if (
    subscription &&
    currentKey &&
    arrayBufferToBase64Url(currentKey) !== arrayBufferToBase64Url(applicationServerKey.buffer)
  ) {
    await subscription.unsubscribe();
    subscription = null;
  }

  subscription ??= await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });

  setPushEnabledFlag(true);
  return toPayload(subscription);
};

/** Desliga o push neste navegador. Devolve o endpoint pra revogar no backend. */
export const unsubscribeFromPush = async (): Promise<string | null> => {
  setPushEnabledFlag(false);
  const subscription = await getCurrentSubscription();
  if (!subscription) return null;
  const { endpoint } = subscription;
  await subscription.unsubscribe();
  return endpoint;
};

/** Para reenviar a inscrição existente ao backend a cada carregamento do hub. */
export const getCurrentSubscriptionPayload = async (): Promise<FidexaHubPushSubscriptionPayload | null> => {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return null;
  const subscription = await getCurrentSubscription();
  if (!subscription) return null;
  setPushEnabledFlag(true);
  return toPayload(subscription);
};

/**
 * Fim da sessão (logout ou sessão derrubada pela API): o aparelho para de
 * receber o push do member que saiu. Sem isso, num celular emprestado as
 * prévias das conversas dele continuavam aparecendo na tela de bloqueio.
 * `sessionToken` null = sessão já inválida; só cancela a inscrição local (o
 * serviço de push passa a responder 410 e o backend revoga sozinho).
 */
export const disablePushOnSessionEnd = async (sessionToken: string | null): Promise<void> => {
  try {
    const endpoint = await unsubscribeFromPush();
    if (endpoint && sessionToken) {
      await fidexaHubNotificationService.revokeSubscription(endpoint, sessionToken);
    }
  } catch {
    // Melhor esforço: o logout nunca pode travar por causa do push.
  }
};
